import config from '../../config';

/**
 * Declension of numerals
 * @param number
 * @param titles - ['найдена', 'найдено', 'найдены'] for ru, ['minute', 'minutes'] for en
 * @returns {String}
 */
export function declOfNums(number, titles) {
  if (config.i18n.lang !== 'ru') {
    return number === 1 ? titles[0] : titles[1];
  }

  const cases = [2, 0, 1, 1, 1, 2];

  const index = (number % 100 > 4 && number % 100 < 20)
    ? 2
    : cases[(number % 10 < 5) ? number % 10 : 5];

  return titles[index];
}

/**
 * Like Number.toFixed()
 * @param value
 * @param decimals
 * @returns {number}
 */
export function round(value, decimals) {
  const factor = decimals ? 10 ** decimals : 1;
  return Math.round(value * factor) / factor;
}

/**
 * Returns a depply cloned object without reference.
 */
export function deepClone(obj) {
  if (Array.isArray(obj)) {
    return obj.map(deepClone);
  }

  if (obj && typeof obj === 'object') {
    const cloned = {};
    const keys = Object.keys(obj);
    for (let i = 0, l = keys.length; i < l; i++) {
      const key = keys[i];
      cloned[key] = deepClone(obj[key]);
    }
    return cloned;
  }

  return obj;
}

export function camelToKebab(string) {
  return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

export function sortArrayOfObjects(array, property, order) {
  const isDescending = order === -1;
  return [...array].sort((a, b) => {
    let sortA = a[property];
    let sortB = b[property];

    if (isDescending) {
      [sortA, sortB] = [sortB, sortA];
    }

    const intSortA = parseInt(sortA, 10);
    const intSortB = parseInt(sortB, 10);
    if (!Number.isNaN(intSortA) && !Number.isNaN(intSortB)) {
      return intSortA - intSortB;
    }

    if (sortA === null && sortB === null) {
      return 0;
    }

    [sortA, sortB] = [sortA, sortB].map((s) => ((s || '').toString().toLocaleLowerCase()));

    if (sortA > sortB) return 1;
    if (sortA < sortB) return -1;

    return 0;
  });
}

export function isObject(variable) {
  return variable !== null && typeof variable === 'object' && Array.isArray(variable) === false;
}

/**
 * Check if value not undefined or null
 * @param value
 * @returns {boolean}
 */
export function isDefined(value) {
  return typeof value !== 'undefined' && value !== null;
}

// DOM

/**
 * Apply callback for each element in list.
 * @param elements
 * @param cb
 * @returns {*}
 */
export function loopElements(elements, cb) {
  let els;

  if (typeof elements === 'string') {
    els = document.querySelectorAll(elements);
  } else if (elements instanceof NodeList) {
    els = elements;
  } else {
    console.warn('elements param must be element selector or NodeList');
    return;
  }

  Array.prototype.forEach.call(els, (el) => cb(el));
}

/**
 * Find closest element
 * Require Element.prototype.matches.
 * @param element
 * @param checker
 * @param last
 * @returns {*}
 */
export function findClosestElement(element, checker, last = document.body) {
  let target = element;
  let lastReached = false;

  while (target && !lastReached) {
    if (typeof checker === 'string' && target.matches(checker)) {
      return target;
    }

    if (typeof checker === 'function' && checker(target)) {
      return target;
    }

    if (target === last) {
      lastReached = true;
    }

    target = target.parentNode;
  }

  return null;
}

/**
 * Clear value of input file
 * @param el
 */
export function clearInputFile(el) {
  if (el.value) {
    try {
      el.value = '';
    } catch (err) { /* nothing */ }

    if (el.value) {
      const form = document.createElement('form');
      const ref = el.nextSibling;
      const parent = el.parentNode;

      form.appendChild(el);
      form.reset();

      if (ref) {
        ref.parentNode.insertBefore(el, ref);
      } else {
        parent.appendChild(el);
      }
    }
  }
}

/**
 * Get element attribute and parse it value
 * @param el
 * @param attr
 * @returns {*}
 */
export function parseAttr(el, attr) {
  const value = el.getAttribute(attr);

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  if (value === 'null') {
    return null;
  }

  // Number
  if (value === `${+value}`) {
    return +value;
  }

  // JSON
  if (/^(?:{[\w\W]*}|\[[\w\W]*])$/.test(value)) {
    try {
      return JSON.parse(value);
    } catch (err) { /* nothing */ }
  }

  return value;
}

/**
 * Fire event on element
 * @param el
 * @param e
 */
export function dispatchEvent(el, e) {
  if ('createEvent' in document) {
    const event = document.createEvent('HTMLEvents');
    event.initEvent(e, false, true);
    el.dispatchEvent(event);
  } else {
    el.fireEvent(`on${e}`);
  }
}

export function getScrollbarWidth() {
  const inner = document.createElement('p');
  inner.style.width = '100%';
  inner.style.height = '200px';

  const outer = document.createElement('div');
  outer.style.position = 'absolute';
  outer.style.top = '0px';
  outer.style.left = '0px';
  outer.style.visibility = 'hidden';
  outer.style.width = '200px';
  outer.style.height = '150px';
  outer.style.overflow = 'hidden';
  outer.appendChild(inner);

  document.body.appendChild(outer);
  const w1 = inner.offsetWidth;
  outer.style.overflow = 'scroll';
  let w2 = inner.offsetWidth;
  if (w1 === w2) w2 = outer.clientWidth;

  document.body.removeChild(outer);

  return w1 - w2;
}

// Other

/**
 * Call function not more than one in ms
 * @param func
 * @param ms
 * @returns {wrapper}
 */
export function throttle(func, ms = 100) {
  let isThrottled = false;
  let savedArgs;
  let savedThis;

  function wrapper(...args) {
    if (isThrottled) {
      savedArgs = args;
      savedThis = this;
      return;
    }

    func.apply(this, args);

    isThrottled = true;

    setTimeout(() => {
      isThrottled = false;

      if (savedArgs) {
        wrapper.apply(savedThis, savedArgs);
        savedArgs = null;
        savedThis = null;
      }
    }, ms);
  }

  return wrapper;
}
